import { useCallback, useEffect, useState } from 'react'
import { isEqual } from 'lodash-es'
import {
  AutopilotTaskDto,
  SearchTaskRequest,
  SearchTasksResponse,
  useSearchAllTasks,
} from '@urbanx/agx.activities.client'
import { TaskUpdatedNotification } from 'types/pushNotifications'
import { doesTaskMatchUpdate, isSameTask } from 'utils/tasks'
import { TaskFilterState } from './TaskFilters/TaskFiltersState'

const PAGE_SIZE = 20

const createApiSafeArray = <T,>(arr?: T[]) => {
  return arr === undefined || arr.length === 0 ? undefined : arr
}

const buildSearchRequest = (
  taskFilters: TaskFilterState,
  continuationToken?: string,
  fresh?: boolean
): SearchTaskRequest => ({
  pageSize: PAGE_SIZE,
  continuationToken: fresh ? undefined : continuationToken,
  requireQueryCount: fresh,
  states: createApiSafeArray(taskFilters.states),
  teamTypes: createApiSafeArray(taskFilters.teamTypes),
  stages: createApiSafeArray(taskFilters.stages),
  agencyIds: createApiSafeArray(taskFilters.agencyIds),
  assignedToUserIds: createApiSafeArray(taskFilters.assignedToUserIds),
  taskDue: taskFilters.taskDue,
  addressSearchText: taskFilters.addressSearchText,
})

const mergeUniqueTasks = (
  existingTasks: AutopilotTaskDto[],
  nextTasks: AutopilotTaskDto[]
) => {
  const mergedTasks = [...existingTasks]

  nextTasks.forEach((task) => {
    if (!mergedTasks.some((candidate) => isSameTask(candidate, task))) {
      mergedTasks.push(task)
    }
  })

  return mergedTasks
}

const applyTaskUpdate = (
  previousTasks: AutopilotTaskDto[],
  taskUpdate: TaskUpdatedNotification
) => {
  const taskIndex = previousTasks.findIndex((task) =>
    doesTaskMatchUpdate(task, taskUpdate)
  )

  if (taskIndex === -1) {
    return previousTasks
  }

  if (taskUpdate.isCompleted) {
    return previousTasks.filter((_, index) => index !== taskIndex)
  }

  const currentTask = previousTasks[taskIndex]
  const updatedTask: AutopilotTaskDto = {
    ...currentTask,
    assignedTo: taskUpdate.assignee,
    dueDate: (taskUpdate.dueDate ?? currentTask.dueDate) as Date | undefined,
    inProgress: taskUpdate.inProgress ?? currentTask.inProgress,
    isCompleted: taskUpdate.isCompleted ?? currentTask.isCompleted,
    isLocked: taskUpdate.isLocked ?? currentTask.isLocked,
    isNew: taskUpdate.isNew ?? currentTask.isNew,
  }

  return [
    ...previousTasks.slice(0, taskIndex),
    updatedTask,
    ...previousTasks.slice(taskIndex + 1),
  ]
}

const useTaskSearch = (
  taskFilters?: TaskFilterState,
  filtersPending?: boolean
) => {
  const [currentFilters, setCurrentFilters] = useState<
    TaskFilterState | undefined
  >(taskFilters)
  const [tasksResponse, setTasksResponse] = useState<
    SearchTasksResponse | undefined
  >(undefined)
  const [tasks, setTasks] = useState<AutopilotTaskDto[]>([])
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)

  const { searchAllTasks, searchAllTasksPending } = useSearchAllTasks({
    onSuccess: (data, request) => {
      setTasksResponse(data)
      setTasks((prev) => mergeUniqueTasks(prev, data?.tasks ?? []))
      if (request?.continuationToken === undefined) {
        setTotalCount(data?.totalCount)
      }
    },
    onError: (error) => {
      console.log(error)
    },
  })

  const search = useCallback(
    (fresh: boolean) => {
      if (!taskFilters) return

      searchAllTasks(
        buildSearchRequest(taskFilters, tasksResponse?.continuationToken, fresh)
      )
    },
    [searchAllTasks, tasksResponse?.continuationToken, taskFilters]
  )

  const refreshSearch = useCallback(() => {
    setTasksResponse(undefined)
    setTasks([])

    search(true)
  }, [search])

  useEffect(() => {
    search(true)
  }, [])

  const loadMore = useCallback(() => {
    if (searchAllTasksPending) return

    if (!tasksResponse?.continuationToken) return

    search(false)
  }, [search, tasksResponse?.continuationToken, searchAllTasksPending])

  useEffect(() => {
    if (filtersPending) return

    if (isEqual(currentFilters, taskFilters)) return

    setCurrentFilters(taskFilters)
    setTasksResponse(undefined)
    setTasks([])

    search(true)
  }, [taskFilters, filtersPending])

  const handleTaskUpdate = useCallback((taskUpdate: TaskUpdatedNotification) => {
    setTasks((prevTasks) => applyTaskUpdate(prevTasks, taskUpdate))
  }, [])

  return {
    hasMore: !!tasksResponse?.continuationToken,
    totalCount: totalCount,
    tasks,
    searchAllTasksPending,
    loadMore,
    handleTaskUpdate,
    refreshSearch,
  }
}

export default useTaskSearch
